import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import cx from 'classnames';
import moment from 'moment';
import clone from 'ramda/src/clone';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Input from '@components/@core/Input';
import Thumbnail from '@components/@core/Thumbnail';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import { getImageURL, getHtmlIframes, getHtmlImages, hasHtml } from '~/services/util';

const Comment = ({ className, data = {}, date = null, children = null, userId, onLike, onReply, loginUser }) => {
    const disableReply = !userId || !data?.data?.reply_id || userId === data?.user_id;
    const isLiked = data.stats?.liked && loginUser ? !!data.stats?.liked[userId]?.user_id : false;
    const fullName = data.user?.first_name + ' ' + data.user?.last_name;
    return (
        <div className={cx('d-flex', className)}>
            <Avatar url={data?.author_avatar} isSponsored={true} />
            <div className="d-flex-1 ms-2">
                <div className="d-h-between mb-3">
                    <div>
                        <p className="dsl-m12 mb-2">{fullName}</p>
                        <p className="dsl-b12 mb-0">{data.user?.profile?.company}</p>
                    </div>
                    {date && (
                        <div className="d-flex ai-center">
                            <span className="dsl-m12">{date}</span>
                        </div>
                    )}
                </div>
                {hasHtml(data.data?.body || '') ? <div dangerouslySetInnerHTML={{ __html: data.data?.body }} /> : <p className="dsl-b14">{data.data?.body}</p>}
                <div className={cx('d-flex mb-3 ai-center', children && 'border-bottom')}>
                    <Button className="d-flex ai-start px-0" type="link" disabled={disableReply} onClick={onReply}>
                        <Icon name="fas fa-reply" size={12} color="#343f4b" />
                        <span className="dsl-m14 ms-2">Reply</span>
                    </Button>
                    <Button className="d-flex ai-start" type="link" onClick={onLike}>
                        <Icon name={`fa${isLiked ? 'r' : 'l'} fa-thumbs-up ms-3`} color={isLiked ? '#376caf' : '#676767'} size={14} />
                        <span className="dsl-m14 ms-2">{data.stats?.likes || 0}</span>
                    </Button>
                    <Button className="d-flex ai-start" type="link">
                        <Icon name="fas fa-comment ms-4" color="#676767" size={14} />
                        <span className="dsl-m14 ms-2">{data.stats?.comments || 0}</span>
                    </Button>
                </div>
                {children}
            </div>
        </div>
    );
};

const removeReplyComments = (comments, replyComments) => {
    replyComments.forEach((replyComment) => {
        const index = comments.findIndex((comment) => comment.id === replyComment.id);
        comments.splice(index, 1);
    });
    return comments;
};

const Detail = ({ data, posts, onRefresh, onSelect }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const commentInputEl = useRef(null);

    const authenticated = useSelector((state) => state.app.token);
    const loginUser = useSelector((state) => state.app.user?.community_user);
    const [comment, setComment] = useState('');
    const [reply, setReply] = useState(null);
    const avatar = useSelector((state) => state.app.avatar);
    const isLiked = data.stats?.liked && loginUser ? !!data.stats?.liked[loginUser.id]?.user_id : false;
    const isCommented = data?.comments && loginUser?.id ? data?.comments.some((comment) => comment.user_id === loginUser?.id) : false;
    const blogId = data.blog.id;
    const recommendedPosts = posts?.slice(0, 3);

    useEffect(() => {
        const payload = {
            data: {
                community_user_id: loginUser?.id,
                events: [{ content_id: data.id, source: 'home', type: 'view' }],
            },
        };
        dispatch(CommunityActions.addcontenteventRequest(payload, false));
    }, [data]);

    useEffect(() => {
        if (!posts[0]) {
            dispatch(CommunityActions.getcomblogpostsRequest({ exclusive: 1, perPage: 3, page: 1 }, 'premium'));
        }
    }, [posts]);

    const handleBlog = () => {
        dispatch(CommunityActions.getcommunityblogRequest({ userId: data.user_id, slug: data?.entity?.slug }));
    };

    const handleComment = () => {
        const payload = {
            userId: data.user_id,
            comment: {
                parent_id: data.id,
                data: { body: comment, reply_id: reply?.data?.reply_id },
            },
        };
        if (authenticated) {
            dispatch(CommunityActions.postsavecommentRequest(payload, handleBlog));
        } else {
            const after = { type: 'POSTSAVECOMMENT_REQUEST', payload, callback: handleBlog };
            handleAuthentication(after);
        }
        setComment(null);
        setReply(null);
    };

    const handleAuthentication = (after) => {
        const route = location;
        const payload = { type: 'Authentication', data: { before: { route, after } } };
        dispatch(AppActions.modalRequest(payload));
    };

    const handleLike = (e) => () => {
        if (isLiked && !!e.stats?.liked[loginUser.id]?.user_id) {
            toast.info(`You already liked this post.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            const payload = { contentId: e.id, flag: 'like' };
            if (authenticated && authenticated !== '') {
                const after = {
                    type: 'GETCOMMUNITYUSER_REQUEST',
                    payload: { userId: e.author?.id ? e.author.id : e.user_id, blogId },
                };
                dispatch(CommunityActions.postcontentflagRequest(payload, null, after, onRefresh));
            } else {
                const after = { type: 'POSTCONTENTFLAG_REQUEST', payload, callback: onRefresh };
                handleAuthentication(after);
            }
        }
    };

    const handleSetSpam = (e) => {
        const payload = { contentId: data.id, flag: 'spam' };
        dispatch(CommunityActions.postcontentflagRequest(payload));
    };

    const handleClickComments = () => {
        commentInputEl?.current && commentInputEl.current.focus();
    };

    const commentArea = (
        <div className="d-flex ssm-column my-3">
            <div className="d-flex mb-2 w-100">
                <Avatar url={avatar} isSponsored={true} />
                <Input
                    className="d-flex-1 ms-2"
                    placeholder="Type a comment..."
                    direction="vertical"
                    as="textarea"
                    rows={3}
                    inputRef={commentInputEl}
                    value={comment}
                    onChange={(e) => setComment(e)}
                />
            </div>
            <div className="flex-column">
                <Button className="ml-3 ssm-l-0 mb-1 w-100" disabled={comment === ''} name="COMMENT" onClick={handleComment} />
                {reply && <Button className="ml-3 ssm-l-0 w-100" type="medium" name="CANCEL" onClick={() => setReply(null)} />}
            </div>
        </div>
    );

    const postComments = useMemo(() => {
        let comments = clone(data.comments || []);

        comments.forEach((item) => {
            item.replyComments = comments.filter((comment) => comment.data.reply_id === item.id && comment.id !== item.id);
            comments = removeReplyComments(comments, item.replyComments);
        });

        return comments;
    }, [data.comments]);

    const handleContactMe = (e) => () => {
        navigate(`/${e.author.entity.slug}/about?user_id=${e.author.id}`);
    };

    const iframes = getHtmlIframes(data.data?.body);
    const images = getHtmlImages(data.data?.body);
    const fullName = `${data.author?.first_name ?? ''} ${data.author?.last_name ?? ''}`;
    const authorId = data.author?.id ?? data.user_id;
    const options = data.stats?.flag ? ['Unflag spam'] : ['Flag spam'];

    return (
        <>
            <Helmet>
                <title>{fullName}'s Blog | DrivingSales</title>
                <meta property="og:type" content="article" />
                <meta property="og:description" content={data.data.seo_description} />
                <meta property="og:title" content={data.name} />
                <meta property="og:image" content={getImageURL(images)} />
            </Helmet>
            <div className="card mt-2">
                <div className="d-h-between">
                    <div className="d-flex ai-center">
                        <Avatar className="mr-2" url={data.author_avatar} isSponsored={true} />
                        <div>
                            <p className="dsl-m12 mb-1">
                                {data.author?.first_name} {data.author?.last_name}
                            </p>
                            <p className="dsl-b12 mb-0">{data.author?.profile?.company}</p>
                        </div>
                    </div>
                    <div className="d-flex">
                        <span className="dsl-m12">{moment(data.created_at).format('MMM DD, YY')}</span>
                        {loginUser?.id && loginUser?.id !== authorId && <EditDropdown className="ml-3" options={options} onChange={handleSetSpam} />}
                    </div>
                </div>
                <p className="dsl-b16 bold mt-4 mb-1">{data.name}</p>
                <div className="blog-detail w-100" dangerouslySetInnerHTML={{ __html: data.data?.body }} />
                {iframes?.map((iframe, idx) => (
                    <div key={idx} className="blog-iframe" dangerouslySetInnerHTML={{ __html: iframe }} />
                ))}
                <div className="d-flex ssm-column border border-5 mt-4 p-3">
                    <Avatar url={data.author_avatar} className="mr-2" size="medium" isSponsored={true} />
                    <div className="ml-3 ssm-l-0">
                        <p className="dsl-b14 text-400 mb-2">
                            {data.author?.first_name} {data.author?.last_name}
                        </p>
                        <p className="dsl-b12 text-400 mb-1">{data.author?.profile?.company}</p>
                        <h6 className="dsl-m12">{data.author?.profile?.job_title}</h6>
                    </div>
                    <div className="d-flex-1 ms-4 ssm-l-0">
                        <p className="dsl-m14 fst-italic word-break" dangerouslySetInnerHTML={{ __html: data.author?.profile?.info_bio }} />
                        {iframes?.map((iframe, idx) => (
                            <p key={idx} className="dsl-m14 fst-italic blog-item" dangerouslySetInnerHTML={{ __html: iframe }} />
                        ))}
                        <Button className="ml-auto" type="link" name="CONTACT ME" onClick={handleContactMe(data)} />
                    </div>
                </div>
                <div className="d-flex ai-center mt-4">
                    <Icon name="fas fa-eye" color="#676767" size={14} />
                    <span className="dsl-m14 ms-2">{data.stats?.views || 0}</span>
                    <Button className="d-h-start" type="link" onClick={handleLike(data)}>
                        <Icon name={`fa${isLiked ? 'r' : 'l'} fa-thumbs-up ms-3`} color={isLiked ? '#376caf' : '#676767'} size={14} />
                        <span className={`${isLiked ? 'dsl-p14 text-500' : 'dsl-m14'} ms-2`}>{data.stats?.likes || 0}</span>
                    </Button>
                    <Button className="d-h-start" type="link" onClick={handleClickComments}>
                        <Icon name={`fa${isLiked ? 'r' : 'l'} fa-comment`} color={isCommented ? '#376caf' : '#676767'} size={14} />
                        <span className={cx('dsl-m14 ms-2', isCommented && 'dsl-p14 text-500')}>{data.stats?.comments || 0}</span>
                    </Button>
                    {/* <Icon name="fas fa-share-square ms-auto" color="#676767" size={14} />
          <span className="dsl-m16 text-400 ms-2">Share</span> */}
                </div>
            </div>
            <div className="card mt-3">
                <p className="dsl-b20 bold">Comments</p>
                {postComments.map((comment, index) => (
                    <Comment
                        key={`c${index}`}
                        data={comment}
                        date={moment(comment.created_at).format('MMM DD, YY')}
                        userId={loginUser?.id}
                        loginUser={loginUser}
                        onLike={handleLike(comment)}
                        onReply={() => setReply(comment)}
                    >
                        {comment.replyComments.map((replyComment, idx) => (
                            <Comment
                                key={`c${idx}`}
                                data={replyComment}
                                date={moment(replyComment.created_at).format('MMM DD, YY')}
                                userId={loginUser?.id}
                                loginUser={loginUser}
                                onLike={handleLike(replyComment)}
                                onReply={() => setReply(replyComment)}
                            >
                                {reply?.id === replyComment.id && commentArea}
                            </Comment>
                        ))}
                        {reply?.id === comment.id && commentArea}
                    </Comment>
                ))}
                {!reply && commentArea}
            </div>
            {/* <div className="card mt-3">
        <div className="d-flex justify-content-between">
          <Button type="link">
            <Icon name="fas fa-chevron-left" color="#376caf" size={14} />
            <span className="dsl-p14 ms-2">PREV POST</span>
          </Button>
          <Button type="link">
            <span className="dsl-p14 me-2">NEXT POST</span>
            <Icon name="fas fa-chevron-right" color="#376caf" size={14} />
          </Button>
        </div>
      </div> */}
            <div className="card mt-3">
                <p className="dsl-b22 bold mb-0">Recommended Posts</p>
                {recommendedPosts.map((item, index) => {
                    if (item.id === data.id) return null;
                    const thumbnail = getImageURL(item.data?.body);
                    return (
                        <div className="posts-list d-flex ssm-column cursor-pointer mt-1 w-100" key={`p${index}`} onClick={() => onSelect(item)}>
                            <Thumbnail className="mw-100" source={thumbnail} size="regular" />
                            <div className="ml-3 ssm-l-0 w-100">
                                <p className="dsl-b16 bold mb-2">{item.name}</p>
                                <div
                                    className="dsl-b16 truncate-two mb-3"
                                    dangerouslySetInnerHTML={{
                                        __html: item.data?.body?.replace(/(<([^>]+)>)/gi, ''),
                                    }}
                                />
                                <div className="d-flex">
                                    <Avatar url={item.author_avatar} isSponsored={true} />
                                    <div className="ml-2">
                                        <p className="dsl-m12 mb-1">
                                            {item.author?.first_name} {item.author?.last_name}
                                        </p>
                                        <p className="dsl-b12 mb-0">{item.author?.profile?.company}</p>
                                    </div>
                                    <span className="dsl-m12 ms-auto">{moment(item.created_at).format('MMM DD, YY')}</span>
                                </div>
                            </div>
                            <hr className="w-100 mobile-screen" />
                        </div>
                    );
                })}
            </div>
        </>
    );
};

Detail.propTypes = {
    data: PropTypes.any.isRequired,
    posts: PropTypes.array,
    onRefresh: PropTypes.func,
    onSelect: PropTypes.func,
};

Detail.defaultProps = {
    data: {},
    posts: [],
    onRefresh: () => {},
    onSelect: () => {},
};

export default Detail;
